import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Upload,
  FileText,
  Search,
  Star,
  CalendarDays,
  Scale,
  Landmark,
  Wallet,
  Bitcoin,
} from "lucide-react";
import { useT, type TranslationKey } from "@/lib/translation";
import { updateUserSettings } from "@/api/client";

// TECH-DEBT: this dev utility runs in every environment (no NODE_ENV guard) and
// ships to production. It should be wrapped in `if (import.meta.env.DEV)` or
// moved to a dev-only module. The block also sits before the UI imports, which
// is unusual code ordering.
// Expose a global function to reset onboarding state for testing
if (typeof window !== "undefined") {
  (
    window as unknown as Window & { resetOnboarding: () => void }
  ).resetOnboarding = () => {
    // onboarding_concept_v1 / onboarding_analysis_v1 are stale keys from before
    // these flags moved server-side — cleared here only to tidy up old browsers.
    // onboarding_celebration_v2 is still the live flag for the celebration modal.
    Object.keys(localStorage)
      .filter(
        (k) =>
          k.startsWith("onboarding_concept_v1") ||
          k.startsWith("onboarding_celebration_v2") ||
          k.startsWith("onboarding_analysis_v1"),
      )
      .forEach((k) => localStorage.removeItem(k));
    updateUserSettings({
      onboarding_welcome_seen: false,
      onboarding_analysis_seen: false,
    })
      .catch(() => {})
      .finally(() => window.location.reload());
  };
}
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

interface FeatureItem {
  icon: typeof FileText;
  labelKey: TranslationKey;
  descriptionKey: TranslationKey;
}

interface OnboardingStep {
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  detailsKey: TranslationKey;
  image?: string;
  items?: FeatureItem[];
}

const ADD_METHODS: FeatureItem[] = [
  {
    icon: RefreshCw,
    labelKey: "onboarding:welcome.addMethods.sync.label",
    descriptionKey: "onboarding:welcome.addMethods.sync.description",
  },
  {
    icon: Upload,
    labelKey: "onboarding:welcome.addMethods.importFile.label",
    descriptionKey: "onboarding:welcome.addMethods.importFile.description",
  },
  {
    icon: FileText,
    labelKey: "onboarding:welcome.addMethods.manual.label",
    descriptionKey: "onboarding:welcome.addMethods.manual.description",
  },
];

const CRYPTO_PATHS: FeatureItem[] = [
  {
    icon: Landmark,
    labelKey: "onboarding:welcome.cryptoPaths.broker.label",
    descriptionKey: "onboarding:welcome.cryptoPaths.broker.description",
  },
  {
    icon: Wallet,
    labelKey: "onboarding:welcome.cryptoPaths.exchange.label",
    descriptionKey: "onboarding:welcome.cryptoPaths.exchange.description",
  },
  {
    icon: Bitcoin,
    labelKey: "onboarding:welcome.cryptoPaths.livePrices.label",
    descriptionKey: "onboarding:welcome.cryptoPaths.livePrices.description",
  },
];

const ANALYSIS_FEATURES: FeatureItem[] = [
  {
    icon: Search,
    labelKey: "onboarding:welcome.analysisFeatures.stockAnalysis.label",
    descriptionKey:
      "onboarding:welcome.analysisFeatures.stockAnalysis.description",
  },
  {
    icon: Star,
    labelKey: "onboarding:welcome.analysisFeatures.watchlists.label",
    descriptionKey:
      "onboarding:welcome.analysisFeatures.watchlists.description",
  },
  {
    icon: CalendarDays,
    labelKey: "onboarding:welcome.analysisFeatures.earningsCalendar.label",
    descriptionKey:
      "onboarding:welcome.analysisFeatures.earningsCalendar.description",
  },
  {
    icon: FileText,
    labelKey: "onboarding:welcome.analysisFeatures.transcripts.label",
    descriptionKey:
      "onboarding:welcome.analysisFeatures.transcripts.description",
  },
  {
    icon: Scale,
    labelKey: "onboarding:welcome.analysisFeatures.dcfCalculators.label",
    descriptionKey:
      "onboarding:welcome.analysisFeatures.dcfCalculators.description",
  },
];

const STEPS: OnboardingStep[] = [
  {
    titleKey: "onboarding:welcome.steps.portfolioPerBrokerage.title",
    descriptionKey:
      "onboarding:welcome.steps.portfolioPerBrokerage.description",
    detailsKey: "onboarding:welcome.steps.portfolioPerBrokerage.details",
    image: "/guides/onboarding/step1.png",
  },
  {
    titleKey: "onboarding:welcome.steps.allPortfolios.title",
    descriptionKey: "onboarding:welcome.steps.allPortfolios.description",
    detailsKey: "onboarding:welcome.steps.allPortfolios.details",
    image: "/guides/onboarding/step2.png",
  },
  {
    titleKey: "onboarding:welcome.steps.addTransactions.title",
    descriptionKey: "onboarding:welcome.steps.addTransactions.description",
    detailsKey: "onboarding:welcome.steps.addTransactions.details",
    items: ADD_METHODS,
  },
  {
    titleKey: "onboarding:welcome.steps.crypto.title",
    descriptionKey: "onboarding:welcome.steps.crypto.description",
    detailsKey: "onboarding:welcome.steps.crypto.details",
    items: CRYPTO_PATHS,
  },
  {
    titleKey: "onboarding:welcome.steps.analysisMode.title",
    descriptionKey: "onboarding:welcome.steps.analysisMode.description",
    detailsKey: "onboarding:welcome.steps.analysisMode.details",
    items: ANALYSIS_FEATURES,
  },
  {
    titleKey: "onboarding:welcome.steps.getStarted.title",
    descriptionKey: "onboarding:welcome.steps.getStarted.description",
    detailsKey: "onboarding:welcome.steps.getStarted.details",
    image: "/guides/onboarding/step3.png",
  },
];

export default function WelcomeModal({ open, onClose }: WelcomeModalProps) {
  const t = useT();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      navigate("/portfolio/portfolios");
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-lg h-[700px] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl">
            {t("onboarding:welcome.title")}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding:welcome.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 overflow-hidden">
          {/* Step indicators */}
          <div className="flex gap-1 flex-shrink-0">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i === currentStep ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex-shrink-0">
              <h2 className="text-lg font-semibold">{t(step.titleKey)}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t(step.descriptionKey)}
              </p>
            </div>

            {/* Screenshot, or feature grid for steps with items - centered with flexible spacing */}
            <div className="flex-1 flex items-center justify-center">
              {step.items ? (
                <div className="grid grid-cols-3 gap-3 w-full">
                  {step.items.map(
                    ({ icon: Icon, labelKey, descriptionKey }) => (
                      <div
                        key={labelKey}
                        className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-center"
                      >
                        <Icon className="h-5 w-5 text-primary mx-auto" />
                        <div className="text-xs font-semibold">
                          {t(labelKey)}
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {t(descriptionKey)}
                        </p>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="rounded-lg border-2 border-muted-foreground/20 overflow-hidden flex items-center justify-center bg-background">
                  <img
                    src={step.image}
                    alt={t(step.titleKey)}
                    className="max-w-full max-h-80 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>

            {/* Details text */}
            <p className="text-sm text-muted-foreground leading-relaxed flex-shrink-0">
              {t(step.detailsKey)}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex-1 text-center text-xs text-muted-foreground">
              {t("onboarding:wizard.stepOf", {
                current: currentStep + 1,
                total: STEPS.length,
              })}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              disabled={isLastStep}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="ghost" onClick={handleSkip} className="flex-1">
              {t("onboarding:wizard.skipIntro")}
            </Button>
            <Button onClick={handleNext} className="flex-1 rounded-2xl">
              {isLastStep
                ? t("onboarding:welcome.createPortfolio")
                : t("onboarding:wizard.next")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
